<template>
    <v-card class="tables-container" outlined>
        <v-card-text>

            <!-- Edit/Delete Sector Buttons -->
            <div v-if="activeSector" class="sector-buttons">
                <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                        <span v-on="on" class="cursor-pointer px-1" @click.stop="editSector">
                            <v-icon size="20" color="primary">fal fa-pen</v-icon>
                        </span>
                    </template>
                    <span>{{ $t('tables_settings.edit_sector') }}</span>
                </v-tooltip>
                <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                        <span v-on="on" class="cursor-pointer px-1" @click.stop="deleteSector">
                            <v-icon size="20" color="error">fal fa-times</v-icon>
                        </span>
                    </template>
                    <span>{{ $t('tables_settings.delete_sector') }}</span>
                </v-tooltip>
            </div>

            <div class="flex">
                <v-tabs
                    v-model="activeTabName"
                    show-arrows
                    background-color="transparent"
                    class="limit-width"
                >
                    <v-tab
                        v-for="sector in sectors"
                        :key="sector.id"
                        :href="`#tab-sector-${sector.id}`"
                    >
                        {{ sector.name }}
                    </v-tab>
                </v-tabs>

                <v-spacer />

                <div class="flex align-items-center">
                    <v-spacer />
                    <v-btn
                        v-if="activeSector"
                        class="mr-1"
                        color="primary"
                        outlined
                        @click="addTable()"
                    >
                        {{ $t('tables_settings.add_table') }}
                    </v-btn>
                    <v-btn
                        class="ml-1"
                        color="primary"
                        outlined
                        @click="addSector"
                    >
                        {{ $t('tables_settings.add_sector') }}
                    </v-btn>
                </div>
            </div>

            <v-tabs-items v-model="activeTabName" touchless class="sector-tabs">
                <v-tab-item
                    v-for="(sector, index) in sectors"
                    :key="sector.id"
                    :ref="`tabSector${index}`"
                    class="sector"
                    touchless
                    :value="`tab-sector-${sector.id}`"
                    :style="{ backgroundImage: `url(${sector.image_url})` }"
                >
                    <table-button
                        v-for="table in sector.tables"
                        :id="'table-' + table.id"
                        :key="table.id"
                        v-resize="onResize"
                        class="draggable"
                        :table="table"
                        :menu-enabled="true"
                        :configurable="true"
                        :parent-height="tableContainer.height"
                        :parent-width="tableContainer.width"
                        @click="selectedTable = $event"
                        @update="updateTable($event)"
                        @delete="deleteTable($event)"
                        @duplicate="duplicateTable($event)"
                    />
                </v-tab-item>
            </v-tabs-items>

            <!-- Sector Add/Edit Dialog -->
            <cod-dialog :show.sync="sectorDialogVisible">
                <!-- Header -->
                <template slot="header">
                    <span v-if="sectorDialogMode == 'add'">{{ $t('tables_settings.add_sector') }}</span>
                    <span v-else>{{ $t('tables_settings.edit_sector') }}</span>
                </template>

                <!-- Content -->
                <div class="pa-5" slot="content">
                    <v-form ref="form" v-model="formValid">
                        <v-container
                            class="grid-list-md"
                            style="overflow-y: hidden"
                            fluid
                        >
                            <v-text-field
                                ref="sectorName"
                                v-model="form.name"
                                outlined
                                :label="$t('tables_settings.sector_name')"
                                :rules="form.rules.name"
                                maxlength="30"
                                autocomplete="off"
                                validate-on-blur
                            />

                            <div class="sales-button__button-image-dialog maxh110 mt-1">
                                <div class="content">
                                    <v-btn text class="upload-btn" @click="$refs.inputUpload.click()">
                                        <v-icon>fal fa-plus</v-icon>
                                    </v-btn>
                                    <input
                                        v-show="false"
                                        ref="inputUpload"
                                        type="file"
                                        accept="image/*"
                                        @change="uploadImage"
                                    >

                                    <div
                                        v-for="(image, index) in sectorImages"
                                        :key="index"
                                        class="image-wrapper"
                                        :class="{ selected: form.image_url === image.image_url }"
                                        @click="form.image = image.path; form.image_url = image.image_url;"
                                    >
                                        <img :src="image.image_url" class="image">
                                    </div>
                                </div>
                            </div>
                        </v-container>
                    </v-form>
                </div>

                <!-- Footer -->
                <template slot="footer">
                    <v-btn class="minw100 flex-1 mr-1" color="primary" outlined @click="sectorDialogVisible = false">
                        {{ $t('general.cancel') }}
                    </v-btn>
                    <v-btn
                        class="minw100 flex-1 ml-1"
                        color="primary"
                        depressed
                        :loading="form.busy"
                        @click="saveSector()"
                    >
                        {{ $t('general.save') }}
                    </v-btn>
                </template>
            </cod-dialog>
        </v-card-text>
    </v-card>
</template>

<script>
import { loadData } from '@/views/shared/loadPageData';
import TableButton from '@/components/TableButton';
import CodDialog from '@/components/CodDialog';
import ApiService from '@/services/ApiService';

export default {
    beforeRouteEnter: (to, from, next) => {
        loadData('/admin/load/table-plan').then(data => next(vm => vm.onDataLoaded(data)));
    },

    components: {
        TableButton,
        CodDialog
    },

    created() {
        this.form = this.createForm();
    },

    beforeDestroy() {
        interact('.draggable').unset();
    },

    mounted() {
        this.$nextTick(() => {
            this.tableContainer = {
                width: document.getElementsByClassName('sector-tabs')[0].offsetWidth,
                height: document.getElementsByClassName('sector-tabs')[0].offsetHeight
            };

            // set tables draggable and resizable
            this.interact();
        });
    },

    data() {
        return {
            activeTabName: null,
            tableContainer: {
                height: 0,
                width: 0
            },

            selectedTable: null,
            sectors: [],
            sectorImages: [],
            sectorDialogVisible: false,
            sectorDialogMode: 'add',
            formValid: false,
            form: this.createForm()
        };
    },

    computed: {
        activeSector() {
            let sector = this.sectors.filter(sector => sector.id == this.activeTabName.split('-')[2]);
            return sector.length ? sector[0] : null;
        }
    },

    watch: {
        sectorDialogVisible: {
            handler() {
                if (this.$refs.form) {
                    if(this.$refs.form) this.$refs.form.resetValidation();

                    if (this.sectorDialogVisible) {
                        this.$nextTick(() => {
                            this.$refs.sectorName.focus();
                        });
                    }
                }
            }
        }
    },

    methods: {
        onDataLoaded(data) {
            this.sectors = data.sectors;
            this.sectorImages = data.sectorImages;
            this.activeTabName = this.sectors.length ? this.sectors[0].name : null;
        },

        onResize() {
            this.tableContainer.width = document.getElementsByClassName('sector-tabs')[0].offsetWidth;
            this.tableContainer.height = document.getElementsByClassName('sector-tabs')[0].offsetHeight;
        },

        createForm(sector = null) {
            return new CodForm({
                name: sector ? sector.name : null,
                image: sector
                    ? sector.image
                    : this.sectorImages && this.sectorImages.length
                        ? this.sectorImages[0].path
                        : null,
                image_url: sector
                    ? sector.image_url
                    : this.sectorImages && this.sectorImages.length
                        ? this.sectorImages[0].image_url
                        : null,       

                rules: {
                    name: [CodForm.rule('required')]
                }
            });
        },

        addSector() {
            this.form = this.createForm();
            this.sectorDialogMode = 'add';
            this.sectorDialogVisible = true;
        },

        editSector() {
            this.form = this.createForm(this.activeSector);
            this.sectorDialogMode = 'edit';
            this.sectorDialogVisible = true;
        },

        saveSector() {
            if (!this.form.name) {
                if(this.$refs.form) this.$refs.form.resetValidation();
                
                setTimeout(() => {
                    this.$refs.form.validate();
                }, 250);

                return false;
            }

            if (this.sectorDialogMode == 'add') {
                ApiService.post('/admin/sectors', this.form).then(response => {
                    this.sectors.push(response.data);
                    this.activeTabName = `tab-sector-${this.sectors.length}`;
                    this.sectorDialogVisible = false;
                    this.$snackbar(this.$t('tables_settings.sector_saved'), 'success');
                });
            } else if (this.sectorDialogMode == 'edit') {
                ApiService.patch(`/admin/sectors/${this.activeSector.id}`, this.form).then(response => {
                    let index = this.sectors.indexOf(this.activeSector);
                    this.sectors[index].name = this.form.name;
                    this.sectors[index].image = this.form.image;
                    this.sectors[index].image_url = this.form.image_url;
                    this.activeTabName = this.form.name;
                    this.sectorDialogVisible = false;
                    this.$snackbar(this.$t('tables_settings.sector_updated'), 'success');
                });
            }
        },

        deleteSector() {
            this.$confirm(
                this.$t('tables_settings.permanently_delete_sector'),
                {
                    buttonTrueText: this.$t('general.confirm'),
                    buttonFalseText: this.$t('general.cancel'),
                    icon: 'fal fa-times',
                    color: 'error',
                    title: this.$t('general.delete')
                },
                result => {
                    if (!result) return;

                    ApiService.delete(`/admin/sectors/${this.activeSector.id}`, {}).then(response => {
                        this.sectors = this.sectors.filter(sector => sector.id != this.activeSector.id);
                        this.activeTabName = this.sectors.length ? this.sectors[0].name : null;
                        this.$snackbar(this.$t('tables_settings.sector_deleted'), 'success');
                    }).catch(error => {
                        const errors = error.response && error.response.data ? error.response.data.errors : null

                        if (errors && errors.length) {
                            this.$snackbar(this.$t(`tables_settings.error.${errors[0].code}`), 'error', {
                                title: this.$t('general.action_failed')
                            });
                        }
                    });
                }
            );
        },

        addTable(table = null) {
            if (!table) {
                table = this.selectedTable ? JSON.parse(JSON.stringify(this.selectedTable)) : {};
                table.pos_left = 0;
                table.pos_top = 0;
                table.width = this.selectedTable ? this.selectedTable.width : (70 / this.tableContainer.width) * 100;
                table.height = this.selectedTable ? this.selectedTable.height : (70 / this.tableContainer.height) * 100;
                table.color = this.selectedTable ? this.selectedTable.color : 'orange';
                table.shape = this.selectedTable ? this.selectedTable.shape : 'rectangular';
                table.sector_id = this.activeSector.id;
            }

            table.name = this.activeSector.tables.length + 1;

            ApiService.post('/admin/tables', table).then(response => {
                table = response.data;
                this.activeSector.tables.push(table);
            });
        },

        updateTable(table) {
            let tableCopy = JSON.parse(JSON.stringify(table));

            let tableEl = document.getElementById('table-' + tableCopy.id);
            let x = tableEl.getAttribute('data-x');
            let y = tableEl.getAttribute('data-y');
            let height = tableEl.offsetHeight;
            let width = tableEl.offsetWidth;

            tableCopy.width = (width / this.tableContainer.width) * 100;
            tableCopy.height = (height / this.tableContainer.height) * 100;

            if (x) tableCopy.pos_left += (x / this.tableContainer.width) * 100;
            if (y) tableCopy.pos_top += (y / this.tableContainer.height) * 100;

            ApiService.patch(`/admin/tables/${tableCopy.id}`, tableCopy).then(response => {
                table = response.data;
                this.selectedTable = table;
            });
        },

        deleteTable(table) {
            ApiService.delete(`/admin/tables/${table.id}`, {}).then(response => {
                this.activeSector.tables = this.activeSector.tables.filter(tb => tb.id != table.id);
            }).catch(error => {
                const errors = error.response && error.response.data ? error.response.data.errors : null

                if (errors && errors.length) {
                    this.$snackbar(this.$t(`tables_settings.error.${errors[0].code}`), 'error', {
                        title: this.$t('general.action_failed')
                    });
                }
            });
        },

        duplicateTable(table) {
            let tableCopy = JSON.parse(JSON.stringify(table));
            tableCopy.id = -1 * table.id;
            tableCopy.pos_left = 0;
            tableCopy.pos_top = 0;

            this.addTable(tableCopy);
        },

        interact() {
            // target elements with the "draggable" class
            interact('.draggable')
                .draggable({
                    // enable inertial throwing
                    inertia: true,

                    // keep the element within the area of it's parent
                    restrict: {
                        restriction: 'parent',
                        endOnly: true,
                        elementRect: { top: 0, left: 0, bottom: 1, right: 1 }
                    },

                    // enable autoScroll
                    autoScroll: true,

                    // call this function on every dragmove event
                    onmove: event => {
                        let target = event.target;

                        // keep the dragged position in the data-x/data-y attributes
                        let x = (parseFloat(target.getAttribute('data-x')) || 0) + event.dx;
                        let y = (parseFloat(target.getAttribute('data-y')) || 0) + event.dy;

                        // translate the element
                        target.style.webkitTransform = target.style.transform = 'translate(' + x + 'px, ' + y + 'px)';

                        // update the posiion attributes
                        target.setAttribute('data-x', x);
                        target.setAttribute('data-y', y);
                    },

                    onend: event => {
                        let table = this.activeSector.tables.filter(
                            table => table.id == event.target.id.split('-')[1]
                        )[0];
                        this.updateTable(table);
                    }
                })
                .resizable({
                    // resize from all edges and corners
                    edges: { left: true, right: true, bottom: true, top: true },

                    // keep the edges inside the parent
                    restrictEdges: {
                        outer: 'parent',
                        endOnly: true
                    },

                    // minimum size
                    restrictSize: {
                        min: { width: 50, height: 50 }
                    },

                    inertia: true
                })
                .on('resizemove', event => {
                    let target = event.target;
                    let x = parseFloat(target.getAttribute('data-x')) || 0;
                    let y = parseFloat(target.getAttribute('data-y')) || 0;

                    // update the element's style
                    target.style.width = event.rect.width + 'px';
                    target.style.height = event.rect.height + 'px';

                    // translate when resizing from top or left edges
                    x += event.deltaRect.left;
                    y += event.deltaRect.top;

                    target.style.webkitTransform = target.style.transform = 'translate(' + x + 'px,' + y + 'px)';

                    target.setAttribute('data-x', x);
                    target.setAttribute('data-y', y);

                    // add size label
                    let sizeLabel = document.querySelector('.size-label');

                    let labelText = Math.round(event.rect.width) + '\u00D7' + Math.round(event.rect.height);

                    if (sizeLabel) {
                        sizeLabel.innerText = labelText;
                        return;
                    }

                    let node = document.createElement('div');
                    node.classList.add('size-label');
                    node.innerText = labelText;
                    node.style.background = getComputedStyle(target.children[1]).background;
                    target.appendChild(node);
                })
                .on('resizeend', event => {
                    const el = document.querySelector('.size-label')
                    if(el) {
                        el.remove();
                    }
                    
                    let table = this.activeSector.tables.filter(table => table.id == event.target.id.split('-')[1])[0];
                    this.updateTable(table);
                });
        },

        // Upload Button Image
        uploadImage(event) {
            let files = event.target.files || event.dataTransfer.files;
            if (!files.length) return;

            let data = { name: event.target.files[0].name, file: null };

            var reader = new FileReader();
            reader.onload = e => {
                data.file = e.target.result;
                ApiService.post('/admin/sectors/images', data).then(response => {
                    let present = false;
                    this.sectorImages.forEach(image => {
                        if (image.image_url === response.data.image_url) {
                            present = true;
                            this.form.image = image.path;
                            this.form.image_url = image.image_url;
                        }
                    });
                    if (!present) {
                        this.sectorImages.unshift(response.data);
                        this.form.image = response.data.path;
                        this.form.image_url = response.data.image_url;
                    }
                });
            };
            reader.readAsDataURL(files[0]);
        }
    }
};
</script>

<style lang="scss" scoped>
.draggable {
    touch-action: none;
}

.tables-container {
    min-width: 300px;
    height: 100%;
    position: relative;

    ::v-deep .v-tab {
        max-width: 120px;
    }

    .limit-width {
        max-width: 200px;

        @media screen and (min-width: 600px) {
            max-width: 300px;
        }

        @media screen and (min-width: 960px) {
            max-width: 400px;
        }

        @media screen and (min-width: 1264px) {
            max-width: 700px;
        }

        @media screen and (min-width: 1600px) {
            max-width: 1000px;
        }
    }

    .sector-tabs {
        position: absolute;
        top: 64px;
        bottom: 10px;
        left: 15px;
        right: 15px;

        & ::v-deep .v-window__container {
            height: 100%;
            overflow: visible;

            .sector {
                position: relative;
                height: 100%;
                width: 100%;
                background: lightgrey;
                background-size: 100% 100%;

                .table-button {
                    .size-label {
                        width: 100%;
                        text-align: center;
                        line-height: 16px;
                        border-radius: 100px;
                        position: absolute;
                        top: 50%;
                        left: 50%;
                        transform: translate(-50%, -50%);
                    }
                }
            }
        }
    }

    .sector-buttons {
        z-index: 1;
        position: absolute;
        top: 70px;
        right: 25px;
        width: 65px;
        height: 40px;
        background: #f9f9f9;
        margin-top: 4px;
        border: 1px solid lightgrey;
        border-radius: 4px;
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .sector-images {
        background: #f9f9f9;
        padding: 10px;
        border: 1px solid #dcdfe6;
        border-radius: 4px;
        display: flex;
        flex-wrap: wrap;

        .image-wrapper {
            width: 148px;
            height: 100px;
            background: white;
            border-radius: 4px;
            border: 1px solid #dcdfe6;
            margin: 10px;

            .image {
                width: 100%;
                height: 100%;
            }

            &:hover {
                border: 3px solid rgba(64, 158, 255, 0.54);
                cursor: pointer;
            }

            &.selected {
                border: 3px solid #409eff;
            }
        }

        & ::v-deep .el-upload--picture-card {
            height: 100px;
            line-height: 105px;
        }
    }
}

.menu {
    max-width: 190px;

    .label {
        width: 100%;
        text-align: center;
        font-weight: bold;
    }

    .image-buttons {
        flex-direction: column;
    }

    .image-align {
        margin: 5px;
        height: 68px;
        width: 68px;
    }
}

.sales-button__button-image-dialog {
    background: white;
    overflow-y: auto;

    .header {
        padding: 10px;
        background: lightgrey;
    }

    .content {
        background: #f9f9f9;
        padding: 10px;
        display: flex;
        flex-wrap: wrap;
        max-height: 320px;
        overflow-y: auto;

        .upload-btn {
            min-width: 0;
            width: 120px;
            height: 80px;
            border: 1px dashed lightgrey;
            font-size: 24px;
            margin: 5px;
        }

        .image-wrapper {
            width: 120px;
            height: 80px;
            background: white;
            border-radius: 4px;
            border: 1px solid #dcdfe6;
            margin: 5px;

            .image {
                width: 100%;
                height: 100%;
            }

            &:hover {
                border: 3px solid rgba(64, 158, 255, 0.54);
                cursor: pointer;
            }

            &.selected {
                border: 3px solid #409eff;
            }
        }
    }
}
</style>
